import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  EyeOff,
  Minus,
  Plus,
  RefreshCw,
  ShieldCheck,
  Undo2,
  Unlink,
} from 'lucide-react';
import { Button, Card, SegmentedControl, Spinner } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { driftAction, type DriftAction } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';

/**
 * Källdrift: sidor på webben som ändrats sedan vi läste in dem.
 *
 * Tabellen kb_source_drift har funnits sedan maj men aldrig haft en vy. Den
 * fylldes på i tysthet och lästes bara som en tidsstämpel på Systemstatus, så
 * varken kansliet eller vi kunde se vad kontrollen faktiskt hittat.
 */

type DriftStatus = 'pending' | 'reviewed' | 'dismissed' | 'updated';

interface DriftRow {
  id: string;
  filename: string;
  checked_at: string;
  text_similarity: number;
  size_change_percent: number;
  chunks_before: number;
  sample_removed: string | null;
  sample_added: string | null;
  fetch_status: number;
  fetch_error: string | null;
  status: DriftStatus;
  reviewed_at: string | null;
  notes: string | null;
}

const STATUS_ETIKETT: Record<DriftStatus, string> = {
  pending: 'Att granska',
  reviewed: 'Kvitterad',
  dismissed: 'Avfärdad',
  updated: 'Omindexerad',
};

function basfraga() {
  return supabase.from('kb_source_drift').select('id', { head: true, count: 'exact' });
}

interface Antal {
  pending: number;
  doda: number;
  reviewed: number;
  updated: number;
}

const LISTGRANS = 300;

export function DriftView() {
  const [items, setItems] = useState<DriftRow[]>([]);
  const [antal, setAntal] = useState<Antal | null>(null);
  const [loading, setLoading] = useState(true);
  const [laddfel, setLaddfel] = useState<string | null>(null);
  const [actioning, setActioning] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<DriftStatus | 'all'>('pending');

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    // Filtreringen MÅSTE ske i databasen. Klientfiltrering inom ett fönster på
    // de 300 nyaste raderna hade fungerat i dag och tystnat med tiden: nästan
    // varje ny rad stängs numera automatiskt av omindexeringen, så de öppna
    // raderna åldras och trillar ut ur fönstret. Det som försvunnit först hade
    // varit källor veckocrawlen aldrig når, alltså exakt de rader vyn finns för.
    const listfraga = supabase
      .from('kb_source_drift')
      .select('*')
      .order('checked_at', { ascending: false })
      .limit(LISTGRANS);

    const lista = statusFilter === 'all' ? listfraga : listfraga.eq('status', statusFilter);

    // Siffrorna räknas också i databasen, av samma skäl: en summering över ett
    // avkapat fönster är inte en summa.
    const rakna = (bygg: (q: ReturnType<typeof basfraga>) => ReturnType<typeof basfraga>) =>
      bygg(basfraga());

    Promise.all([
      lista,
      rakna((q) => q.eq('status', 'pending')),
      rakna((q) => q.eq('status', 'pending').not('fetch_error', 'is', null)),
      rakna((q) => q.eq('status', 'reviewed')),
      rakna((q) => q.eq('status', 'updated')),
    ]).then(([listUtfall, pending, doda, reviewed, updated]) => {
      if (!mounted) return;

      // Ett misslyckat anrop får ALDRIG rendera som en tom, lugnande kö.
      // Samma fälla fanns i luck-triagen: "Inga öppna luckor" med grön bock
      // när anropet fallerat är ett fel utklätt till besked.
      if (listUtfall.error) {
        setLaddfel(listUtfall.error.message);
        setItems([]);
      } else {
        setLaddfel(null);
        setItems((listUtfall.data ?? []) as DriftRow[]);
      }

      // null = kunde inte räknas. Rutorna visar streck i stället för nollor,
      // annars hade de påstått "0 att granska" ovanför en felruta.
      const raknefel = [pending, doda, reviewed, updated].some((r) => r.error);
      setAntal(
        raknefel
          ? null
          : {
              pending: pending.count ?? 0,
              doda: doda.count ?? 0,
              reviewed: reviewed.count ?? 0,
              updated: updated.count ?? 0,
            },
      );

      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [refreshKey, statusFilter]);

  const act = async (row: DriftRow, action: DriftAction) => {
    setActioning(row.id);
    try {
      await driftAction(row.id, action);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setActioning(null);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatPill label="Att granska" value={antal?.pending ?? null} highlight />
        <StatPill label="Döda länkar" value={antal?.doda ?? null} highlight />
        <StatPill label="Kvitterade" value={antal?.reviewed ?? null} />
        <StatPill label="Lösta av omindexering" value={antal?.updated ?? null} />
      </div>

      <Card variant="glass" className="p-6 flex flex-col min-h-[420px]">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex-1">
            <h2 className="font-bold text-ink-900 text-[15px] leading-none">
              {items.length} {items.length === 1 ? 'källa' : 'källor'}
            </h2>
            <p className="text-[11px] font-semibold text-ink-500 mt-1">
              Sidor som ändrats på webben sedan kunskapsbasen läste in dem
            </p>
          </div>
          <SegmentedControl
            label="Filtrera källdrift på status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as DriftStatus | 'all')}
            segments={[
              { value: 'pending', label: 'Att granska' },
              { value: 'reviewed', label: 'Kvitterade' },
              { value: 'updated', label: 'Omindexerade' },
              { value: 'all', label: 'Alla' },
            ]}
          />
        </div>

        <Forklaring />

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size={22} className="text-brand-500" />
          </div>
        ) : laddfel ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
            <AlertTriangle size={34} strokeWidth={1.5} className="text-red-600 mb-4" />
            <h3 className="text-display text-[17px] text-ink-900">Kunde inte hämta driftkön</h3>
            <p className="text-[13px] text-ink-500 max-w-md leading-relaxed mt-2">
              Listan nedan är alltså inte tom – den gick inte att läsa. {laddfel}
            </p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-4"
              leftIcon={<RefreshCw size={14} strokeWidth={1.75} />}
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              Försök igen
            </Button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState statusFilter={statusFilter} />
        ) : (
          <ul className="flex-1 space-y-3 overflow-y-auto -mx-1 px-1">
            {items.map((row) => (
              <DriftCard
                key={row.id}
                row={row}
                actioning={actioning === row.id}
                onAction={act}
              />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function Forklaring() {
  return (
    <div className="mb-4 rounded-xl border border-ink-200 bg-ink-50/60 px-4 py-3">
      <p className="text-[11.5px] text-ink-600 leading-relaxed">
        Kontrollen hämtar varje webbkälla på nytt och jämför med texten i kunskapsbasen.{' '}
        <strong className="text-ink-800">En sida som veckocrawlen når rättar sig själv</strong> – när
        källan indexeras om stängs raden automatiskt och hamnar under Omindexerade. Det som blir kvar
        att granska är källor crawlen inte når och sidor där inläsningen misslyckas gång på gång.
      </p>
    </div>
  );
}

function DriftCard({
  row,
  actioning,
  onAction,
}: {
  row: DriftRow;
  actioning: boolean;
  onAction: (row: DriftRow, action: DriftAction) => void;
}) {
  const dodLank = !!row.fetch_error;
  const likhet = Number(row.text_similarity);
  const storlek = Number(row.size_change_percent);

  return (
    <li className="rounded-xl border border-ink-200 bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {dodLank ? (
              <Unlink size={14} strokeWidth={2} className="text-red-600 shrink-0" />
            ) : (
              <AlertTriangle size={14} strokeWidth={2} className="text-amber-600 shrink-0" />
            )}
            <a
              href={`https://${row.filename}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] font-bold text-ink-900 hover:text-brand-700 truncate inline-flex items-center gap-1"
            >
              <span className="truncate">{row.filename}</span>
              <ExternalLink size={11} strokeWidth={2} className="shrink-0" />
            </a>
          </div>
          <p className="text-[11px] text-ink-500 mt-1">
            Kontrollerad {formatDate(row.checked_at)}
            {row.status !== 'pending' && ` · ${STATUS_ETIKETT[row.status]}`}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {dodLank ? (
            <Matvarde etikett="Svar" varde={row.fetch_status ? String(row.fetch_status) : '–'} varning />
          ) : (
            <>
              <Matvarde etikett="Likhet" varde={likhet.toFixed(2)} varning={likhet < 0.5} />
              <Matvarde
                etikett="Storlek"
                varde={`${storlek > 0 ? '+' : ''}${storlek.toFixed(0)} %`}
                varning={Math.abs(storlek) > 40}
              />
            </>
          )}
        </div>
      </div>

      {dodLank && (
        <p className="mt-3 text-[11.5px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Sidan gick inte att hämta: {row.fetch_error}. Innehållet ligger kvar i kunskapsbasen som det
          såg ut vid den senaste lyckade inläsningen.
        </p>
      )}

      {!dodLank && (row.sample_removed || row.sample_added) && (
        <div className="mt-3 space-y-1.5">
          {row.sample_removed && <Utdrag riktning="bort" text={row.sample_removed} />}
          {row.sample_added && <Utdrag riktning="till" text={row.sample_added} />}
        </div>
      )}

      {row.notes && row.status !== 'pending' && (
        <p className="mt-3 text-[11px] text-ink-500 leading-relaxed">{row.notes}</p>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-ink-100">
        {row.status === 'pending' ? (
          <>
            <Button
              size="sm"
              leftIcon={<Check size={14} strokeWidth={2} />}
              onClick={() => onAction(row, 'reviewed')}
              disabled={actioning}
            >
              Kvittera
            </Button>
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<EyeOff size={14} strokeWidth={1.75} />}
              onClick={() => onAction(row, 'dismissed')}
              disabled={actioning}
            >
              Avfärda
            </Button>
          </>
        ) : row.status === 'updated' ? (
          <p className="text-[11.5px] text-ink-500 inline-flex items-center gap-1.5">
            <ShieldCheck size={13} strokeWidth={2} className="text-emerald-700" />
            Stängd av omindexeringen – inget att göra
          </p>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Undo2 size={14} strokeWidth={1.75} />}
            onClick={() => onAction(row, 'reopen')}
            disabled={actioning}
          >
            Öppna igen
          </Button>
        )}
        {actioning && <Spinner size={14} className="text-ink-500" />}
      </div>
    </li>
  );
}

function Matvarde({ etikett, varde, varning }: { etikett: string; varde: string; varning?: boolean }) {
  return (
    <div className="text-right">
      <p className="text-[9px] font-bold uppercase tracking-wider text-ink-500">{etikett}</p>
      <p
        className={cn(
          'text-[13px] font-bold tabular-nums leading-none mt-1',
          varning ? 'text-red-700' : 'text-ink-800',
        )}
      >
        {varde}
      </p>
    </div>
  );
}

function Utdrag({ riktning, text }: { riktning: 'bort' | 'till'; text: string }) {
  const bort = riktning === 'bort';
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg px-3 py-2 border',
        bort ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200',
      )}
    >
      {bort ? (
        <Minus size={12} strokeWidth={2.5} className="text-red-700 mt-0.5 shrink-0" />
      ) : (
        <Plus size={12} strokeWidth={2.5} className="text-emerald-800 mt-0.5 shrink-0" />
      )}
      <p className={cn('text-[11.5px] leading-relaxed', bort ? 'text-red-800' : 'text-emerald-900')}>
        {text}
      </p>
    </div>
  );
}

function StatPill({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number | null;
  highlight?: boolean;
}) {
  return (
    <Card
      variant="glass"
      className={cn('p-4', highlight && value !== null && value > 0 && 'border-amber-200 bg-amber-50/40')}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">{label}</p>
      <p className="text-display text-[22px] text-ink-900 tabular-nums leading-none mt-2">
        {value === null ? '–' : value.toLocaleString('sv-SE')}
      </p>
    </Card>
  );
}

function EmptyState({ statusFilter }: { statusFilter: DriftStatus | 'all' }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
      <ShieldCheck size={36} strokeWidth={1.25} className="text-ink-500 mb-4" />
      <h3 className="text-display text-[17px] text-ink-900">
        {statusFilter === 'pending' ? 'Inga källor att granska' : 'Inga källor här'}
      </h3>
      <p className="text-[13px] text-ink-500 max-w-sm leading-relaxed mt-2">
        {statusFilter === 'pending'
          ? 'Ingen webbkälla har drivit ifrån kunskapsbasen sedan den senaste kontrollen. Kör om drift-kollen från Systemstatus för en färsk mätning.'
          : 'Byt filter för att se andra rader.'}
      </p>
    </div>
  );
}
